import { Link } from "react-router-dom";

/**
 * Diplomexa brand logo.
 *
 * Defaults to the **wordmark-only** crop — it reads cleanly at every height
 * from `h-7` (footer) to `h-12` (navbar), and avoids the muddiness of trying
 * to fit the full illustrated brand mark into a tiny pixel budget.
 *
 * Props:
 *   - `hero` (default false): show the full illustrated brand mark (globe +
 *     podium + wordmark). Use this on landing heroes / login splash / splash
 *     screens where there is real estate. Don't use it in 48px navbars.
 *   - `light` (default false): use the dark-mode variant. Required on any
 *     navy / dark hero section so the wordmark is white instead of navy.
 *   - `linkable` (default true): wrap the image in a `<Link>` (target controlled by `to`).
 *   - `to` (default `/`): the route the wordmark links to.  Pass
 *     `"/dashboard"` when rendering inside an authenticated workspace so
 *     clicking the logo doesn't bounce the user back to the public marketing
 *     site (which feels like a logout — prod incident Feb 2026).
 */
export default function BrandLogo({ hero = false, light = false, linkable = true, compact = false, to = "/" }) {
  // Back-compat: callers that pass the old `compact` prop expect the wordmark
  // (which is now the default), so we just respect that for clarity.
  const useWordmark = !hero || compact;

  const src = useWordmark
    ? (light ? "/brand/diplomexa-wordmark-light.png" : "/brand/diplomexa-wordmark.png")
    : (light ? "/brand/diplomexa-brand-mark-dark.png" : "/brand/diplomexa-brand-mark.png");

  // Sensible default heights per use-case
  const heightClass = useWordmark ? "h-9" : "h-20 md:h-24";

  const img = (
    <img
      src={src}
      alt="Diplomexa"
      data-testid="brand-logo"
      className={`${heightClass} w-auto object-contain select-none`}
      draggable={false}
    />
  );

  if (!linkable) return img;
  return (
    <Link to={to} className="inline-flex items-center" data-testid="brand-logo-link">
      {img}
    </Link>
  );
}
